import React, { useRef, useState, useCallback, memo, forwardRef, useImperativeHandle } from 'react';
import { Box, Flex, Stack, Text } from '@chakra-ui/react';

import { useVideoPlayer } from '@/hooks/useVideoPlayer';

import { CandidateWithJobContext } from '@/types/Candidate';
import {
  AlertsAndFollowUpQuestion,
  InterviewAnalysisResult,
  ScreeningAnalysisResult
} from '@/types/AnalysisResult';
import { Job } from '@/types/Job';
import { Meeting, MeetingSummary } from '@/types/Meeting';
import { Screening } from '@/types/Screening';
import { InterviewAnalysisSection, isMeetingNotStarted, ScreeningStatus } from '@/types/enums';

import ErrorBoundary from '@/components/layout/ErrorBoundary';

import {
  AnalysisInProgress,
  InterviewEmptyState,
  InterviewNotStarted,
  InterviewQNAsSection,
  InterviewSummarySection,
  InterviewTranscriptSection,
  ScreeningPlaybackSection,
  ScreeningQNAsSection,
  ScreeningScheduled,
  ScreeningSummarySection
} from '@/views/InterviewAnalysis';

// Types
interface Section {
  label: string;
  value: InterviewAnalysisSection;
}

interface InterviewAnalysisIndexProps {
  job: Job;
  candidate: CandidateWithJobContext;
  meeting: Meeting | null;
  screening: Screening | null;
  analysisResult: InterviewAnalysisResult | ScreeningAnalysisResult | null;
  followUpQuestions: AlertsAndFollowUpQuestion[];
  onUpdateItem: (index: number, item: AlertsAndFollowUpQuestion | null) => Promise<boolean>;
  section: InterviewAnalysisSection;
  setSection: (section: InterviewAnalysisSection) => void;
  onAddInterview: () => void;
  onEditInterview: (updatedMeeting: MeetingSummary) => void;
  onEditScreening: (screening: Screening) => void;
}

export interface InterviewAnalysisIndexRef {
  scrollToFollowUpItems: () => void;
}

// Constants
const interviewSections: Section[] = [
  {
    label: 'Summary',
    value: InterviewAnalysisSection.Summary
  },
  {
    label: 'Q&As',
    value: InterviewAnalysisSection.QNAs
  },
  {
    label: 'Transcript',
    value: InterviewAnalysisSection.Transcript
  }
];

const screeningSections: Section[] = [
  {
    label: 'Summary',
    value: InterviewAnalysisSection.Summary
  },
  {
    label: 'Q&As',
    value: InterviewAnalysisSection.QNAs
  },
  {
    label: 'Playback',
    value: InterviewAnalysisSection.Playback
  }
];

const InterviewAnalysisIndex = forwardRef<InterviewAnalysisIndexRef, InterviewAnalysisIndexProps>(
  (
    {
      job,
      candidate,
      meeting,
      screening,
      analysisResult,
      followUpQuestions,
      onUpdateItem,
      section,
      setSection,
      onAddInterview,
      onEditInterview,
      onEditScreening
    },
    ref
  ) => {
    const followUpItemsRef = useRef<HTMLDivElement | null>(null);

    const screeningVideoPlayer = useVideoPlayer();
    const [selectedScreeningQuestionIndex, setSelectedScreeningQuestionIndex] = useState(0);

    useImperativeHandle(ref, () => ({
      scrollToFollowUpItems: () => {
        if (followUpItemsRef.current) {
          followUpItemsRef.current.scrollIntoView({ behavior: 'smooth' });
        }
      }
    }));

    const handleSectionChange = useCallback(
      (newSection: InterviewAnalysisSection) => {
        setSection(newSection);
      },
      [setSection]
    );

    const handleQuestionClick = useCallback(
      (index: number) => {
        setSelectedScreeningQuestionIndex(index);
        // Reset player when switching questions
        if (screeningVideoPlayer.playerRef.current) {
          screeningVideoPlayer.playerRef.current.seekTo(0);
        }
      },
      [screeningVideoPlayer.playerRef]
    );

    if (meeting) {
      if (isMeetingNotStarted(meeting.status)) {
        return (
          <ErrorBoundary>
            <InterviewNotStarted
              meeting={meeting}
              job={job}
              candidate={candidate}
              onEditInterview={onEditInterview}
            />
          </ErrorBoundary>
        );
      }

      if (!analysisResult) {
        return (
          <ErrorBoundary>
            <AnalysisInProgress />
          </ErrorBoundary>
        );
      }

      return (
        <ErrorBoundary>
          <Flex w="100%" mt="32px" gap="24px">
            <Box
              w="240px"
              minW="240px"
              h="100%"
              border="1px solid"
              borderColor="neutrals.200"
              borderRadius="12px"
              p="16px 12px"
            >
              <Stack>
                {interviewSections.map((item) => (
                  <Box
                    key={item.value}
                    p="16px 24px"
                    borderRadius="12px"
                    bg={item.value === section ? 'neutrals.100' : 'transparent'}
                    cursor="pointer"
                    onClick={() => handleSectionChange(item.value)}
                  >
                    <Text
                      color="neutrals.700"
                      fontSize="14px"
                      fontWeight={item.value === section ? 'semibold' : 'normal'}
                    >
                      {item.label}
                    </Text>
                  </Box>
                ))}
              </Stack>
            </Box>
            {section === InterviewAnalysisSection.Summary && (
              <InterviewSummarySection
                analysisResult={analysisResult as InterviewAnalysisResult}
                followUpQuestions={followUpQuestions}
                onUpdateItem={onUpdateItem}
                followUpItemsRef={followUpItemsRef}
              />
            )}
            {section === InterviewAnalysisSection.QNAs && (
              <InterviewQNAsSection analysisResult={analysisResult as InterviewAnalysisResult} />
            )}
            {section === InterviewAnalysisSection.Transcript && (
              <InterviewTranscriptSection meeting={meeting} />
            )}
          </Flex>
        </ErrorBoundary>
      );
    } else if (screening) {
      if (screening.status === ScreeningStatus.Scheduled) {
        return (
          <ErrorBoundary>
            <ScreeningScheduled screening={screening} onEditScreening={onEditScreening} />
          </ErrorBoundary>
        );
      }

      if (!analysisResult) {
        return (
          <ErrorBoundary>
            <AnalysisInProgress />
          </ErrorBoundary>
        );
      }

      return (
        <ErrorBoundary>
          <Flex w="100%" mt="32px" gap="24px">
            <Stack>
              <Box
                w="240px"
                minW="240px"
                h="fit-content"
                border="1px solid"
                borderColor="neutrals.200"
                borderRadius="12px"
                p="16px 12px"
              >
                <Stack>
                  {screeningSections.map((item) => (
                    <Box
                      key={item.value}
                      p="16px 24px"
                      borderRadius="12px"
                      bg={item.value === section ? 'neutrals.100' : 'transparent'}
                      cursor="pointer"
                      onClick={() => handleSectionChange(item.value)}
                    >
                      <Text
                        color="neutrals.700"
                        fontSize="14px"
                        fontWeight={item.value === section ? 'semibold' : 'normal'}
                      >
                        {item.label}
                      </Text>
                    </Box>
                  ))}
                </Stack>
              </Box>
              {section == InterviewAnalysisSection.Playback && (
                <Box w="240px" minW="240px" mt="32px" maxH="300px" overflowY="auto">
                  <Stack gap="0">
                    {screening?.questions.map((_, index) => (
                      <React.Fragment key={index}>
                        <Box
                          p="4px 14px"
                          cursor="pointer"
                          borderRadius="8px"
                          onClick={() => handleQuestionClick(index)}
                          _hover={{
                            color: 'neutrals.500'
                          }}
                        >
                          <Flex alignItems="center" gap="8px">
                            <Box
                              w="8px"
                              h="8px"
                              p="0"
                              borderRadius="50%"
                              bg={
                                selectedScreeningQuestionIndex === index
                                  ? 'primary.900'
                                  : 'neutrals.300'
                              }
                            />
                            <Text
                              fontSize="14px"
                              color={
                                selectedScreeningQuestionIndex === index
                                  ? 'neutrals.900'
                                  : 'neutrals.600'
                              }
                              fontWeight={
                                selectedScreeningQuestionIndex === index ? 'semibold' : 'normal'
                              }
                            >
                              Question {index + 1}
                            </Text>
                          </Flex>
                        </Box>
                        {index < screening.questions.length - 1 && (
                          <Box
                            ml="16px"
                            height="20px"
                            borderLeft="2px solid"
                            borderColor="neutrals.200"
                            alignSelf="flex-start"
                          />
                        )}
                      </React.Fragment>
                    ))}
                  </Stack>
                </Box>
              )}
            </Stack>
            {section === InterviewAnalysisSection.Summary && (
              <ScreeningSummarySection
                screeningType={screening.screening_type}
                analysisResult={analysisResult as ScreeningAnalysisResult}
                followUpQuestions={followUpQuestions}
                onUpdateItem={onUpdateItem}
                followUpItemsRef={followUpItemsRef}
              />
            )}
            {section === InterviewAnalysisSection.QNAs && (
              <ScreeningQNAsSection screening={screening} />
            )}
            {section === InterviewAnalysisSection.Playback && (
              <ScreeningPlaybackSection
                screening={screening}
                selectedQuestionIndex={selectedScreeningQuestionIndex}
                player={screeningVideoPlayer}
              />
            )}
          </Flex>
        </ErrorBoundary>
      );
    } else {
      return (
        <ErrorBoundary>
          <InterviewEmptyState onAddInterview={onAddInterview} job={job} candidate={candidate} />
        </ErrorBoundary>
      );
    }
  }
);

InterviewAnalysisIndex.displayName = 'InterviewAnalysis';

export default memo(InterviewAnalysisIndex);
